import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import type { FormEvent } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Card } from "@alascorporation/ui"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import { GroupStudentsView } from "../../components/admin/GroupStudentsView"
import { GroupCredentialPanel } from "../../components/admin/GroupCredentialPanel"
import { Modal } from "../../components/ui/modal"
import type { PendingMember } from "../../components/admin/PendingMemberReconcileRow"
import { CheckRow, inputCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { SeedbedShell, type SeedbedTabKey } from "../../components/programs/SeedbedShell"
import { SeedbedInfoForm, EMPTY_SEEDBED_FORM, type SeedbedFormFields } from "../../components/programs/SeedbedInfoForm"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { SearchMultiSelect } from "../../components/admin/SearchMultiSelect"
import { EyeIcon, PencilIcon, TrashBinIcon, LockIcon } from "../../icons"
import { LoadState } from "@/components/ui/LoadState"

// ── Types ──────────────────────────────────────────────────────────────────

type SeedbedStatus = "active" | "paused" | "archived"
type SeedbedStudentStatus = "active" | "suspended" | "ended"

type SeedbedGroup = {
  id: string
  name: string
  description?: string | null
  groupType: 'individual' | 'shared' | 'team'
  cccdTeamId?: string | null
  year?: number | null
  allowGroupLogin?: boolean
  credential?: { loginCode: string; isActive: boolean } | null
  students_count: number
  createdAt: string
  // cccd-groups-view-show-pending-teams: equipo CCCD ya asignado a este
  // semillero por su categoría, sin SeedbedGroup propio todavía (aún no
  // activado ni con miembros) — entrada de solo lectura, sin inscripciones
  // reales, distinta de un grupo ya creado.
  isPending?: boolean
}

type SeedbedDetail = {
  id: string
  name: string
  slug: string
  status: SeedbedStatus
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  institution: { id: string; name: string }
  courses: Array<{ courseId: string; course: { id: string; title: string; slug: string } }>
  lessons: Array<{ lessonId: string; courseId: string; lesson: { id: string; title: string; courseId: string } }>
  enrollments: Array<{
    id: string
    userId: string
    status: SeedbedStudentStatus
    accessStartAt: string
    accessEndAt?: string | null
    assignedAt: string
    user: { id: string; name: string; email: string }
    group?: { id: string; name: string; groupType: string } | null
  }>
}

type CertificateTemplateSummary = {
  id: string
  name: string
  mode: 'individual' | 'group'
  seedbedId: string | null
}

type Course = { id: string; title: string; category?: string | null; difficultyLevel?: string | null }

const difficultyLabels: Record<string, string> = {
  beginner: "Básico",
  intermediate: "Intermedio",
  advanced: "Avanzado",
}

type Lesson = { id: string; title: string }

type Student = { id: string; name: string; email: string }

// ── Helpers ────────────────────────────────────────────────────────────────

function toDateInput(value?: string | null): string {
  if (!value) return ""
  return value.slice(0, 10)
}

function toIsoDate(date: string): string | undefined {
  if (!date) return undefined
  return new Date(`${date}T00:00:00.000Z`).toISOString()
}

const SEEDBED_STATUS_LABELS: Record<SeedbedStatus, string> = {
  active: "Activo",
  paused: "Pausado",
  archived: "Archivado",
}

/** Qué significa cada estado para decidir si vale la pena seguir invirtiendo en este semillero. */
const SEEDBED_STATUS_HINTS: Record<SeedbedStatus, string> = {
  active: "En funcionamiento: la organización lo está usando.",
  paused: "Sin actividad: decide si reactivarlo o archivarlo.",
  archived: "Cerrado: ya no está disponible para estudiantes.",
}

function enrollmentStatusBadge(status: SeedbedStudentStatus): string {
  if (status === "active") return "bg-[color:rgba(34,197,94,0.14)] text-success-700 dark:text-success-300"
  if (status === "suspended") return "bg-[color:rgba(234,179,8,0.16)] text-[var(--alas-gold)]"
  return "bg-[color:rgba(239,68,68,0.14)] text-error-600 dark:text-error-300"
}

// ── Component ──────────────────────────────────────────────────────────────

const SEEDBED_TAB_KEYS: SeedbedTabKey[] = ["info", "groups", "courses"]

export default function SeedbedDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()

  const [detail, setDetail] = useState<SeedbedDetail | null>(null)
  const [infoForm, setInfoForm] = useState<SeedbedFormFields>(EMPTY_SEEDBED_FORM)
  const [savingInfo, setSavingInfo] = useState(false)
  const [infoError, setInfoError] = useState("")
  const [groups, setGroups] = useState<SeedbedGroup[]>([])
  const [groupYearFilter, setGroupYearFilter] = useState<number | 'all'>('all')
  const [groupsPage, setGroupsPage] = useState(1)
  const [credentialGroupId, setCredentialGroupId] = useState<string | null>(null)
  const [pendingGroupsToAssign, setPendingGroupsToAssign] = useState<{ id: string; name: string }[]>([])
  const [assigningGroups, setAssigningGroups] = useState(false)
  const [pendingCourses, setPendingCourses] = useState<Course[]>([])
  const [coursesPage, setCoursesPage] = useState(1)
  const [courses, setCourses] = useState<Course[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [courseLessonsByCourseId, setCourseLessonsByCourseId] = useState<Record<string, Lesson[]>>({})

  const [loading, setLoading] = useState(true)
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [loadingLessons, setLoadingLessons] = useState(false)
  const [savingGroupStudents, setSavingGroupStudents] = useState(false)
  const [savingCourses, setSavingCourses] = useState(false)
  const [savingLessons, setSavingLessons] = useState(false)
  const [updatingStudentStatusId, setUpdatingStudentStatusId] = useState<string | null>(null)

  const initialTab = SEEDBED_TAB_KEYS.includes(searchParams.get("tab") as SeedbedTabKey)
    ? (searchParams.get("tab") as SeedbedTabKey)
    : "groups"
  const [activeTab, setActiveTab] = useState<SeedbedTabKey>(initialTab)
  const [viewingGroupId, setViewingGroupId] = useState<string | null>(null)
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([])
  const [certTemplates, setCertTemplates] = useState<CertificateTemplateSummary[]>([])
  const [assigningTemplateId, setAssigningTemplateId] = useState(false)
  const [unassigningGroupId, setUnassigningGroupId] = useState<string | null>(null)
  const [groupToUnassign, setGroupToUnassign] = useState<SeedbedGroup | null>(null)
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([])
  const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([])
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([])
  const [togglingGroupLogin, setTogglingGroupLogin] = useState(false)

  const [isConfiguringLessons, setIsConfiguringLessons] = useState(false)
  const [lessonSearch, setLessonSearch] = useState("")

  // ── Data loading ───────────────────────────────────────────────────────

  const loadGroups = useCallback(async (seedbedId: string) => {
    setLoadingGroups(true)
    try {
      const res = await api.get(`/academy/seedbeds/admin/${seedbedId}/groups`)
      setGroups(res.data.data ?? [])
    } catch {
      setGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }, [])

  const loadLessonsForCourses = useCallback(async (courseIds: string[]) => {
    if (courseIds.length === 0) {
      setCourseLessonsByCourseId({})
      return
    }
    setLoadingLessons(true)
    try {
      const responses = await Promise.all(courseIds.map(cId => api.get(`/academy/lessons/course/${cId}`)))
      const next: Record<string, Lesson[]> = {}
      courseIds.forEach((cId, i) => { next[cId] = responses[i].data?.data ?? [] })
      setCourseLessonsByCourseId(next)
    } catch {
      setCourseLessonsByCourseId({})
    } finally {
      setLoadingLessons(false)
    }
  }, [])

  const loadDetail = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const [detailRes, coursesRes, studentsRes] = await Promise.all([
        api.get(`/academy/seedbeds/admin/${id}`),
        api.get("/academy/courses/admin/all?limit=100&page=1"),
        api.get("/admin/users?role=student&limit=100&page=1"),
      ])
      const d = detailRes.data?.data as SeedbedDetail
      setDetail(d)
      setInfoForm({
        institution_id: d.institution.id,
        name: d.name,
        slug: d.slug,
        description: d.description ?? "",
        status: d.status,
        start_date: toDateInput(d.startDate),
        end_date: toDateInput(d.endDate),
      })
      setCourses(coursesRes.data.data?.courses ?? [])
      setStudents(studentsRes.data.data?.users ?? [])
      const courseIds = d.courses.map(c => c.courseId)
      setSelectedCourseIds(courseIds)
      setSelectedLessonIds(d.lessons.map(l => l.lessonId))
      void loadLessonsForCourses(courseIds)
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [id, loadLessonsForCourses, loadGroups])

  const loadCertTemplates = useCallback(async () => {
    try {
      const res = await api.get("/academy/certificate-templates")
      setCertTemplates(res.data.data ?? [])
    } catch {
      setCertTemplates([])
    }
  }, [])

  useEffect(() => { void loadCertTemplates() }, [loadCertTemplates])

  async function onAssignTemplate(templateId: string) {
    if (!id) return
    setAssigningTemplateId(true)
    try {
      // Desasigna cualquier plantilla previa de este semillero, luego asigna la elegida.
      const previouslyAssigned = certTemplates.filter(t => t.seedbedId === id && t.id !== templateId)
      await Promise.all(previouslyAssigned.map(t => api.put(`/academy/certificate-templates/${t.id}`, { seedbed_id: null })))
      if (templateId) await api.put(`/academy/certificate-templates/${templateId}`, { seedbed_id: id })
      toastSuccess('Plantilla de certificado actualizada')
      await loadCertTemplates()
    } catch (err) {
      toastError(err)
    } finally {
      setAssigningTemplateId(false)
    }
  }

  useEffect(() => { void loadDetail() }, [loadDetail])

  const loadPendingMembers = useCallback(async (groupId: string) => {
    if (!id) return
    try {
      const res = await api.get(`/academy/seedbeds/admin/${id}/pending-members?groupId=${groupId}`)
      setPendingMembers(res.data.data ?? [])
    } catch {
      setPendingMembers([])
    }
  }, [id])

  useEffect(() => {
    if (viewingGroupId) void loadPendingMembers(viewingGroupId)
    else setPendingMembers([])
  }, [viewingGroupId, loadPendingMembers])

  async function onAddPendingMember(name: string, email: string) {
    if (!id || !viewingGroupId) return
    try {
      await api.post(`/academy/seedbeds/admin/${id}/pending-members`, { name, email, group_id: viewingGroupId })
      await loadPendingMembers(viewingGroupId)
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    }
  }

  async function onReconcilePendingMember(memberId: string, userId: string) {
    if (!id || !viewingGroupId) return
    try {
      await api.post(`/academy/seedbeds/admin/pending-members/${memberId}/reconcile`, { user_id: userId })
      toastSuccess('Integrante vinculado a su cuenta')
      await loadPendingMembers(viewingGroupId)
      await loadDetail()
    } catch (err) {
      toastError(err)
    }
  }

  async function onToggleGroupLogin(enabled: boolean) {
    if (!id || !viewingGroupId) return
    setTogglingGroupLogin(true)
    try {
      await api.put(`/academy/seedbeds/admin/${id}/groups/${viewingGroupId}`, { allow_group_login: enabled })
      toastSuccess(enabled ? 'Login de equipo habilitado' : 'Login de equipo deshabilitado')
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    } finally {
      setTogglingGroupLogin(false)
    }
  }

  // Años presentes entre los grupos de equipo CCCD (cccd-seedbed-team-sync) —
  // filtro local, sin round-trip: los grupos ya están cargados en memoria.
  const groupYears = useMemo(
    () => Array.from(new Set(groups.map((g) => g.year).filter((y): y is number => y != null))).sort((a, b) => b - a),
    [groups]
  )
  const visibleGroups = useMemo(
    () => (groupYearFilter === 'all' ? groups : groups.filter((g) => g.year === groupYearFilter)),
    [groups, groupYearFilter]
  )

  const GROUPS_PAGE_SIZE = 10
  const groupColumns: DataTableColumn<SeedbedGroup>[] = [
    {
      key: "name",
      label: "Grupo",
      render: (group) => (
        <>
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 dark:text-white">{group.name}</p>
            {group.isPending && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Pendiente de activación
              </span>
            )}
          </div>
          {group.description && <p className="text-xs text-gray-500">{group.description}</p>}
        </>
      ),
    },
    {
      key: "groupType",
      label: "Tipo",
      render: (group) => (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          group.groupType === 'shared'
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : group.groupType === 'team'
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
        }`}>
          {group.groupType === 'shared' ? 'Compartido' : group.groupType === 'team' ? `Equipo CCCD${group.year ? ` · ${group.year}` : ''}` : 'Individual'}
        </span>
      ),
    },
    {
      key: "students_count",
      label: "Estudiantes",
      render: (group) =>
        group.isPending ? (
          <span className="text-sm italic text-gray-500 dark:text-gray-400">Sin inscripciones aún</span>
        ) : (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {group.students_count} estudiante{group.students_count !== 1 ? 's' : ''}
          </span>
        ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (group) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={
              group.isPending
                ? [
                    group.cccdTeamId
                      ? { label: "Ver equipo", icon: <EyeIcon />, onClick: () => navigate(`/cccd/teams/${group.cccdTeamId}`) }
                      : null,
                  ].filter((a): a is NonNullable<typeof a> => a !== null)
                : [
                    { label: "Ver estudiantes", icon: <EyeIcon />, onClick: () => setViewingGroupId(group.id) },
                    group.allowGroupLogin
                      ? {
                          label: group.credential?.isActive ? "Restablecer contraseña" : "Generar credenciales",
                          icon: <LockIcon />,
                          onClick: () => setCredentialGroupId(group.id),
                          requires: "academy:seedbeds:edit",
                        }
                      : null,
                    group.groupType === 'team' && group.cccdTeamId
                      ? { label: "Ver equipo", icon: <EyeIcon />, onClick: () => navigate(`/cccd/teams/${group.cccdTeamId}`) }
                      : { label: "Editar", icon: <PencilIcon />, onClick: () => onStartEditGroup(group), requires: "academy:seedbeds:edit" },
                    group.groupType === 'team'
                      ? { label: "Quitar", icon: <TrashBinIcon />, onClick: () => {}, disabled: true, requires: "academy:seedbeds:edit" }
                      : {
                          label: unassigningGroupId === group.id ? "Quitando..." : "Quitar de este semillero",
                          icon: <TrashBinIcon />,
                          onClick: () => setGroupToUnassign(group),
                          variant: "danger",
                          disabled: unassigningGroupId === group.id,
                          requires: "academy:seedbeds:edit",
                        },
                  ].filter((a): a is NonNullable<typeof a> => a !== null)
            }
          />
        </div>
      ),
    },
  ]

  const COURSES_PAGE_SIZE = 10
  const assignedCourses = selectedCourseIds
    .map(cid => courses.find(c => c.id === cid))
    .filter((c): c is Course => Boolean(c))
  const courseColumns: DataTableColumn<Course>[] = [
    {
      key: "title",
      label: "Curso",
      render: (course) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{course.title}</p>
          {course.category && <p className="text-xs text-gray-500">{course.category}</p>}
        </>
      ),
    },
    {
      key: "difficultyLevel",
      label: "Nivel",
      render: (course) => (
        <span className="text-xs text-gray-500">
          {course.difficultyLevel ? difficultyLabels[course.difficultyLevel] ?? course.difficultyLevel : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (course) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: savingCourses ? "Quitando..." : "Quitar de este semillero",
                icon: <TrashBinIcon />,
                onClick: () => void onRemoveCourse(course.id),
                variant: "danger",
                disabled: savingCourses,
                requires: "academy:seedbeds:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  async function onSaveInfo(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!id) return
    setSavingInfo(true)
    setInfoError("")
    try {
      await api.put(`/academy/seedbeds/admin/${id}`, {
        name: infoForm.name,
        slug: infoForm.slug,
        description: infoForm.description || undefined,
        status: infoForm.status,
        start_date: toIsoDate(infoForm.start_date),
        end_date: toIsoDate(infoForm.end_date),
      })
      qc.invalidateQueries({ queryKey: ["server-table", "/academy/seedbeds/admin"] })
      toastSuccess("Semillero actualizado")
      await loadDetail()
    } catch (err) {
      setInfoError(getErrorMessage(err))
    } finally {
      setSavingInfo(false)
    }
  }

  // ── Group handlers ─────────────────────────────────────────────────────
  // Crear/editar un grupo y gestionar su roster ahora vive en el gestor
  // global de grupos (seedbed-group-manager: un grupo es una entidad
  // reutilizable entre semilleros, no un formulario local de esta página).

  function onStartEditGroup(group: SeedbedGroup) {
    navigate(`/instructor/groups/${group.id}`)
  }

  async function onAssignGroupsToSeedbed() {
    if (!id || pendingGroupsToAssign.length === 0) return
    setAssigningGroups(true)
    try {
      await Promise.all(
        pendingGroupsToAssign.map(g => api.post(`/academy/seedbeds/admin/groups/${g.id}/seedbeds`, { seedbed_id: id }))
      )
      toastSuccess(`${pendingGroupsToAssign.length} grupo${pendingGroupsToAssign.length > 1 ? "s" : ""} asignado${pendingGroupsToAssign.length > 1 ? "s" : ""} a este semillero`)
      setPendingGroupsToAssign([])
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    } finally {
      setAssigningGroups(false)
    }
  }

  function toggleGroupToAssign(group: { id: string; name: string }) {
    setPendingGroupsToAssign(prev =>
      prev.some(g => g.id === group.id) ? prev.filter(g => g.id !== group.id) : [...prev, group]
    )
  }

  // "Eliminar" aquí solo quita el grupo de ESTE semillero — el grupo (y su
  // roster) sigue existiendo si está asignado a otros. Borrarlo por completo
  // se hace desde el gestor global de grupos.
  async function onUnassignGroup(groupId: string) {
    if (!id) return
    setUnassigningGroupId(groupId)
    try {
      await api.delete(`/academy/seedbeds/admin/groups/${groupId}/seedbeds/${id}`)
      toastSuccess('Grupo removido de este semillero')
      if (viewingGroupId === groupId) setViewingGroupId(null)
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    } finally {
      setUnassigningGroupId(null)
      setGroupToUnassign(null)
    }
  }

  async function onAssignStudentsToGroup() {
    if (!id || !viewingGroupId) return
    if (selectedStudents.length === 0) { toastError('Selecciona al menos un estudiante'); return }
    setSavingGroupStudents(true)
    try {
      await api.post(`/academy/seedbeds/admin/${id}/groups/${viewingGroupId}/students`, {
        user_ids: selectedStudents.map(s => s.id),
        status: 'active',
      })
      toastSuccess('Estudiantes asignados al grupo')
      setSelectedStudents([])
      await loadDetail()
      await loadGroups(id)
    } catch (err) {
      toastError(err)
    } finally {
      setSavingGroupStudents(false)
    }
  }

  async function onUpdateStudentStatus(userId: string, status: SeedbedStudentStatus) {
    if (!id) return
    setUpdatingStudentStatusId(userId)
    try {
      await api.put(`/academy/seedbeds/admin/${id}/students/${userId}`, { status })
      await loadDetail()
    } catch (err) {
      toastError(err)
    } finally {
      setUpdatingStudentStatusId(null)
    }
  }

  // ── Course/lesson handlers ─────────────────────────────────────────────

  function toggleLesson(lessonId: string) {
    setSelectedLessonIds(prev =>
      prev.includes(lessonId) ? prev.filter(x => x !== lessonId) : [...prev, lessonId]
    )
  }

  async function saveCourseIds(courseIds: string[]) {
    if (!id) return
    setSavingCourses(true)
    try {
      await api.post(`/academy/seedbeds/admin/${id}/courses`, { course_ids: courseIds })
      setSelectedCourseIds(courseIds)
      setSelectedLessonIds([])
      setCourseLessonsByCourseId({})
      await loadDetail()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingCourses(false)
    }
  }

  async function onAddCourses() {
    if (pendingCourses.length === 0) return
    await saveCourseIds([...selectedCourseIds, ...pendingCourses.map(c => c.id)])
    toastSuccess(`${pendingCourses.length} curso${pendingCourses.length > 1 ? "s" : ""} asignado${pendingCourses.length > 1 ? "s" : ""}`)
    setPendingCourses([])
  }

  async function onRemoveCourse(courseId: string) {
    await saveCourseIds(selectedCourseIds.filter(cid => cid !== courseId))
    toastSuccess("Curso quitado del semillero")
  }

  function toggleCourseSelection(course: Course) {
    setPendingCourses(prev => prev.some(c => c.id === course.id) ? prev.filter(c => c.id !== course.id) : [...prev, course])
  }

  async function onAssignLessons() {
    if (!id || selectedLessonIds.length === 0) return
    setSavingLessons(true)
    try {
      await api.post(`/academy/seedbeds/admin/${id}/lessons`, { lesson_ids: selectedLessonIds })
      toastSuccess("Lecciones visibles actualizadas")
      setIsConfiguringLessons(false)
      await loadDetail()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingLessons(false)
    }
  }

  async function onResetLessonsVisibility() {
    if (!id || selectedCourseIds.length === 0) return
    setSavingLessons(true)
    try {
      await api.post(`/academy/seedbeds/admin/${id}/courses`, { course_ids: selectedCourseIds })
      setSelectedLessonIds([])
      toastSuccess("Visibilidad restablecida: acceso completo")
      setIsConfiguringLessons(false)
      await loadDetail()
    } catch (err) {
      toastError(err)
    } finally {
      setSavingLessons(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadState label="Cargando semillero..." />
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
        <p role="alert" aria-live="assertive" className="text-gray-500">Semillero no encontrado.</p>
        <button type="button" onClick={() => navigate("/programs/seedbeds")}
          className="min-h-11 text-sm text-brand-500 hover:underline">← Volver a semilleros</button>
      </div>
    )
  }

  const credentialGroup = credentialGroupId ? groups.find((g) => g.id === credentialGroupId) ?? null : null

  return (
    <>
    <SeedbedShell
      mode="edit"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Semilleros", to: "/programs/seedbeds" },
        detail.name,
      ]}
      title={detail.name}
      subtitle={`${detail.institution.name} · ${SEEDBED_STATUS_LABELS[detail.status]}${
        detail.status !== "active" ? ` — ${SEEDBED_STATUS_HINTS[detail.status]}` : ""
      }`}
      metrics={[
        { label: "Grupos", value: String(groups.length) },
        {
          label: "Estudiantes",
          value: String(detail.enrollments.length),
          tone: detail.enrollments.length === 0 ? "warning" : "neutral",
          hint: detail.enrollments.length === 0 ? "Sin estudiantes inscritos aún" : undefined,
        },
        {
          label: "Cursos asignados",
          value: String(selectedCourseIds.length),
          tone: selectedCourseIds.length === 0 ? "warning" : "neutral",
          hint: selectedCourseIds.length === 0 ? "Los estudiantes no verán cursos" : undefined,
        },
      ]}
    >
      {/* TAB: Grupos */}
      {activeTab === 'groups' && (
        <div className="space-y-4">
          {viewingGroupId === null ? (
            <>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    Grupos asignados {groups.length > 0 ? `(${groups.length})` : ""}
                  </h3>
                  {groupYears.length > 0 && (
                    <select
                      value={groupYearFilter}
                      onChange={(e) => {
                        setGroupYearFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
                        setGroupsPage(1)
                      }}
                      className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800"
                    >
                      <option value="all">Todos los años</option>
                      {groupYears.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="mb-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Busca y asigna grupos ya existentes a este semillero — para crear un grupo nuevo, hazlo desde "Grupos" en el menú.
                </p>

                <SearchMultiSelect
                  selectedOptions={pendingGroupsToAssign.map(g => ({ id: g.id, label: g.name }))}
                  onToggle={(option) => toggleGroupToAssign({ id: option.id, name: option.label })}
                  onSearch={async (query) => {
                    const res = await api.get("/academy/seedbeds/admin/groups", { params: { search: query, limit: 20 } })
                    type GroupRow = { id: string; name: string; groupType: string }
                    const results: GroupRow[] = res.data?.data?.groups ?? []
                    const assignedIds = new Set(groups.map(g => g.id))
                    return results
                      .filter(g => g.groupType !== 'team' && !assignedIds.has(g.id))
                      .map(g => ({ id: g.id, label: g.name }))
                  }}
                  placeholder="Buscar grupo por nombre..."
                  emptyMessage="No hay grupos con ese criterio."
                />

                {pendingGroupsToAssign.length > 0 && (
                  <button type="button" disabled={assigningGroups}
                    onClick={() => void onAssignGroupsToSeedbed()}
                    className="mt-3 min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                    {assigningGroups ? "Asignando..." : `Asignar ${pendingGroupsToAssign.length} grupo${pendingGroupsToAssign.length > 1 ? "s" : ""}`}
                  </button>
                )}
              </div>

              <DataTable
                columns={groupColumns}
                rows={visibleGroups.slice((groupsPage - 1) * GROUPS_PAGE_SIZE, groupsPage * GROUPS_PAGE_SIZE)}
                rowKey={(group) => group.id}
                loading={loadingGroups}
                emptyMessage="Sin grupos asignados."
                page={groupsPage}
                pageSize={GROUPS_PAGE_SIZE}
                total={visibleGroups.length}
                onPageChange={setGroupsPage}
              />
            </>
          ) : (
            <GroupStudentsView
              group={groups.find(g => g.id === viewingGroupId) ?? null}
              seedbedId={id ?? ''}
              enrollments={detail.enrollments.filter(e => e.group?.id === viewingGroupId)}
              pendingMembers={pendingMembers}
              reconcileCandidates={students}
              onAddPendingMember={onAddPendingMember}
              onReconcilePendingMember={onReconcilePendingMember}
              selectedStudents={selectedStudents}
              excludeStudentIds={detail.enrollments.map(e => e.userId)}
              updatingStudentStatusId={updatingStudentStatusId}
              savingGroupStudents={savingGroupStudents}
              onBack={() => setViewingGroupId(null)}
              onToggleStudent={(student) => setSelectedStudents(prev =>
                prev.some(s => s.id === student.id) ? prev.filter(s => s.id !== student.id) : [...prev, student]
              )}
              onAssign={() => void onAssignStudentsToGroup()}
              onUpdateStatus={onUpdateStudentStatus}
              enrollmentStatusBadge={enrollmentStatusBadge}
              toDateInput={toDateInput}
              onToggleGroupLogin={onToggleGroupLogin}
              togglingGroupLogin={togglingGroupLogin}
            />
          )}
        </div>
      )}

      {/* TAB: Cursos */}
      {activeTab === 'courses' && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              Cursos asignados {selectedCourseIds.length > 0 ? `(${selectedCourseIds.length})` : ""}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Busca y selecciona los cursos que verán los estudiantes de este semillero.
            </p>

            <SearchMultiSelect
              selectedOptions={pendingCourses.map(c => ({
                id: c.id,
                label: c.title,
                sublabel: c.difficultyLevel ? difficultyLabels[c.difficultyLevel] ?? c.difficultyLevel : undefined,
              }))}
              onToggle={(option) => {
                const existing = pendingCourses.find(c => c.id === option.id) ?? courses.find(c => c.id === option.id)
                if (existing) toggleCourseSelection(existing)
              }}
              onSearch={async (query) => {
                const q = query.trim().toLowerCase()
                return courses
                  .filter(c => !selectedCourseIds.includes(c.id))
                  .filter(c => c.title.toLowerCase().includes(q))
                  .map(c => ({
                    id: c.id,
                    label: c.title,
                    sublabel: c.difficultyLevel ? difficultyLabels[c.difficultyLevel] ?? c.difficultyLevel : undefined,
                  }))
              }}
              placeholder="Buscar curso por nombre..."
              emptyMessage="No hay cursos con ese criterio."
            />

            {pendingCourses.length > 0 && (
              <button type="button" disabled={savingCourses}
                onClick={() => void onAddCourses()}
                className="mt-3 min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
                {savingCourses ? "Asignando..." : `Asignar ${pendingCourses.length} curso${pendingCourses.length > 1 ? "s" : ""}`}
              </button>
            )}

            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-800">
              <p className="text-xs text-gray-500">
                {selectedLessonIds.length > 0 ? `${selectedLessonIds.length} lecciones curadas` : "Acceso completo por curso"}
              </p>
              <button type="button" disabled={selectedCourseIds.length === 0}
                onClick={() => { void loadLessonsForCourses(selectedCourseIds); setIsConfiguringLessons(true) }}
                className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300">
                Configurar lecciones visibles
              </button>
            </div>

            {isConfiguringLessons && (
              <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Lecciones visibles</h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Selecciona las lecciones que verán los estudiantes de este semillero.
                </p>
                <input
                  placeholder="Buscar lección..."
                  value={lessonSearch}
                  onChange={e => setLessonSearch(e.target.value)}
                  className={`${inputCls} mt-3`}
                />
                <div className="mt-3 max-h-72 overflow-y-auto space-y-4">
                  {loadingLessons ? (
                    <LoadState label="Cargando lecciones..." />
                  ) : (
                    Object.entries(courseLessonsByCourseId).map(([courseId, courseLessons]) => {
                      const courseObj = courses.find(c => c.id === courseId)
                      const filteredLessons = lessonSearch.trim()
                        ? courseLessons.filter(l => l.title.toLowerCase().includes(lessonSearch.toLowerCase()))
                        : courseLessons
                      if (filteredLessons.length === 0) return null
                      return (
                        <div key={courseId}>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {courseObj?.title ?? courseId}
                          </p>
                          <div className="space-y-0.5 pl-2">
                            {filteredLessons.map(lesson => (
                              <CheckRow key={lesson.id}
                                checked={selectedLessonIds.includes(lesson.id)}
                                onChange={() => toggleLesson(lesson.id)}
                                label={lesson.title} />
                            ))}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
                <div className="mt-4 flex justify-between">
                  <button type="button" onClick={() => void onResetLessonsVisibility()} disabled={savingLessons}
                    className={`min-h-11 ${btnSecondary} disabled:opacity-60`}>
                    Restablecer acceso completo
                  </button>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setIsConfiguringLessons(false)} className={`min-h-11 ${btnSecondary}`}>
                      Cancelar
                    </button>
                    <button type="button" onClick={() => void onAssignLessons()} disabled={savingLessons}
                      className={`min-h-11 ${btnSave} disabled:opacity-60`}>
                      {savingLessons ? "Guardando..." : "Guardar visibilidad"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DataTable
            columns={courseColumns}
            rows={assignedCourses.slice((coursesPage - 1) * COURSES_PAGE_SIZE, coursesPage * COURSES_PAGE_SIZE)}
            rowKey={(course) => course.id}
            emptyMessage="Sin cursos asignados."
            page={coursesPage}
            pageSize={COURSES_PAGE_SIZE}
            total={assignedCourses.length}
            onPageChange={setCoursesPage}
          />

        </div>
      )}

      {/* TAB: Información */}
      {activeTab === 'info' && (
        <div className="space-y-5">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            La organización no se puede cambiar después de crear el semillero.
          </p>
          <form id="seedbed-info-form" onSubmit={onSaveInfo}>
            <SeedbedInfoForm form={infoForm} setForm={setInfoForm} institutions={[]} isCreate={false} />
          </form>

          <Card className="rounded-[28px] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Certificado del semillero</h4>
              <button type="button" onClick={() => navigate(`/instructor/certificate-templates/new?seedbedId=${id}`)}
                className="text-xs font-medium text-brand-600 hover:underline">
                + Nueva plantilla para este semillero
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Elige qué plantilla se usa al emitir certificados de este semillero (individuales o de grupo).
            </p>
            <select
              value={certTemplates.find(t => t.seedbedId === id)?.id ?? ""}
              disabled={assigningTemplateId}
              onChange={(e) => void onAssignTemplate(e.target.value)}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm dark:border-gray-700 dark:bg-gray-900"
            >
              <option value="">Sin plantilla asignada (se usará la plantilla global si existe)</option>
              {certTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.name} — {t.mode === 'group' ? 'certificado de grupo' : 'individual'}</option>
              ))}
            </select>
          </Card>

          {infoError ? (
            <p role="alert" aria-live="assertive" className="text-sm font-medium text-error-500">
              {infoError}
            </p>
          ) : null}
          <div className="flex justify-end border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="submit" form="seedbed-info-form" disabled={savingInfo} className={`min-h-11 ${btnSave}`}>
              {savingInfo ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}

    </SeedbedShell>

    <Modal
      isOpen={credentialGroup !== null}
      onClose={() => {
        setCredentialGroupId(null)
        if (id) void loadGroups(id)
      }}
      ariaLabelledBy="credential-group-heading"
      className="max-w-md p-6"
    >
      {credentialGroup && (
        <>
          <h3 id="credential-group-heading" className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">{credentialGroup.name}</h3>
          <GroupCredentialPanel
            seedbedId={id}
            groupId={credentialGroup.id}
            existingLoginCode={credentialGroup.credential?.isActive ? credentialGroup.credential.loginCode : null}
          />
        </>
      )}
    </Modal>

    <ConfirmDialog
      isOpen={groupToUnassign !== null}
      title={`¿Quitar "${groupToUnassign?.name ?? ''}" de este semillero?`}
      message="Se eliminarán las inscripciones de sus estudiantes aquí."
      confirmLabel="Quitar"
      confirming={unassigningGroupId === groupToUnassign?.id}
      onConfirm={() => groupToUnassign && void onUnassignGroup(groupToUnassign.id)}
      onCancel={() => setGroupToUnassign(null)}
    />
    </>
  )
}
